import Link from "next/link";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/ui/Reveal";

const steps = [
  {
    title: "Check your level",
    description: "Take a short placement check to understand what you already know.",
  },
  {
    title: "Choose your goal",
    description: "Tell us your role, industry, interests, and what you want to accomplish.",
  },
  {
    title: "Get your path",
    description: "inaraX recommends learning that's relevant to your level and goals.",
  },
  {
    title: "Keep progressing",
    description: "Your learning evolves as your skills and AI itself evolve.",
  },
] as const;

/** Find my learning path (#find-your-path): the hero's first button lands here. Four numbered steps, then on to the courses. */
export default function FindYourPath() {
  return (
    <section
      id="find-your-path"
      aria-labelledby="find-your-path-heading"
      className="scroll-mt-20 py-size-xl bg-mkt-surface-container-low border-y border-mkt-outline-variant/20"
    >
      <div className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop">
        <Reveal className="max-w-3xl">
          <span className="font-label-md text-label-md text-mkt-primary uppercase tracking-widest">
            Find my learning path
          </span>
          <h2
            id="find-your-path-heading"
            className="mt-size-sm font-headline-lg text-headline-lg md:text-display-lg text-mkt-on-surface"
          >
            Not sure where to start?
          </h2>
          <p className="mt-size-sm font-body-lg text-body-lg text-mkt-on-surface-variant">
            Tell us where you are. We&rsquo;ll help you figure out where to go next.
          </p>
        </Reveal>

        <ol className="list-none pl-0 ml-0 mt-size-lg grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-gutter">
          {steps.map(({ title, description }, index) => (
            <li key={title}>
              <Reveal
                delayMs={index === 0 ? undefined : index === 1 ? 100 : index === 2 ? 200 : 300}
                className="h-full rounded-xl border-t-4 border-mkt-primary-container bg-white p-size-md shadow-[0_4px_16px_rgba(0,60,185,0.10)]"
              >
                <span
                  aria-hidden="true"
                  className="font-display-lg text-[40px] leading-none font-bold text-mkt-primary-container"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-size-sm font-headline-md text-headline-sm font-bold text-mkt-on-surface">{title}</h3>
                <p className="mt-size-xs font-body-md text-body-md text-mkt-on-surface-variant">{description}</p>
              </Reveal>
            </li>
          ))}
        </ol>

        <Reveal className="mt-size-lg">
          <Link
            href="/courses"
            className="group inline-flex items-center gap-2 bg-mkt-primary-container text-mkt-on-primary font-headline-md text-body-lg px-size-lg py-size-sm rounded-lg primary-cta-glow transition-all hover:scale-105 active:scale-95"
          >
            Get Started
            <ArrowRight
              aria-hidden="true"
              className="h-[1em] w-[1em] transition-transform duration-300 group-hover:translate-x-1"
            />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
